import {
  CHANNEL_LABELS,
  COMMITTEE_ROLE_LABELS,
  COMMITTEE_ROLES,
  cleanPhone,
  type MemberFormValues,
  type NotifyChannel,
  PHONE_HINT,
} from '@horof/shared';
import { Button, Form, Input, Radio, Select } from 'antd';

const phoneRule = {
  validator: (_: unknown, value: string | undefined) => {
    const result = cleanPhone(value ?? '');
    return 'error' in result
      ? Promise.reject(new Error(result.error))
      : Promise.resolve();
  },
};

/**
 * A member's details on the phone (doc 67): the same fields and rules as the
 * desk's form, one per line, large.
 */
export function MemberForm({
  initialValues,
  saving,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initialValues: Partial<MemberFormValues>;
  saving: boolean;
  submitLabel: string;
  onSubmit: (values: MemberFormValues) => void;
  onCancel?: () => void;
}) {
  const [form] = Form.useForm<MemberFormValues>();
  const contactPhone = Form.useWatch('contactPhone', form);

  return (
    <Form
      form={form}
      layout="vertical"
      size="large"
      initialValues={{
        sex: 'MALE',
        notifyChannel: 'WHATSAPP',
        committeeRole: null,
        ...initialValues,
      }}
      onFinish={onSubmit}
      className="card member-form"
      requiredMark={false}
    >
      <Form.Item
        name="firstName"
        label="الاسم الشخصي"
        rules={[
          { required: true, whitespace: true, message: 'الاسم الشخصي مطلوب' },
        ]}
      >
        <Input autoComplete="off" />
      </Form.Item>
      <Form.Item name="fatherName" label="اسم الأب (اختياري)">
        <Input autoComplete="off" />
      </Form.Item>
      <Form.Item
        name="lastName"
        label="الاسم العائلي"
        rules={[
          { required: true, whitespace: true, message: 'الاسم العائلي مطلوب' },
        ]}
      >
        <Input autoComplete="off" />
      </Form.Item>
      <Form.Item name="sex" label="الجنس">
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          options={[
            { value: 'MALE', label: 'ذكر' },
            { value: 'FEMALE', label: 'أنثى' },
          ]}
        />
      </Form.Item>
      <Form.Item
        name="memberAddress"
        label="العنوان (اختياري)"
        extra="إن تُرك فارغاً، يُكتب عنوان الدوار المحدَّد في الإعدادات."
      >
        <Input autoComplete="off" />
      </Form.Item>

      <Form.Item
        name="phone"
        label="هاتف العضو"
        rules={[phoneRule]}
        extra={PHONE_HINT}
      >
        <Input type="tel" className="ltr-input" autoComplete="off" />
      </Form.Item>
      <Form.Item
        name="contactPhone"
        label="هاتف قريب تُرسل إليه الرسائل (اختياري)"
        rules={[phoneRule]}
        extra="إن وُجد، تُرسل الفواتير والتذكيرات إلى هذا الرقم بدل رقم العضو."
      >
        <Input type="tel" className="ltr-input" autoComplete="off" />
      </Form.Item>
      {contactPhone?.trim() && (
        <Form.Item
          name="contactName"
          label="صاحب هذا الرقم، مثلاً «ابنه عبد الله»"
        >
          <Input autoComplete="off" />
        </Form.Item>
      )}
      <Form.Item name="notifyChannel" label="طريقة التواصل">
        <Radio.Group
          className="vertical-radios"
          options={(Object.keys(CHANNEL_LABELS) as NotifyChannel[]).map(
            (c) => ({
              value: c,
              label: CHANNEL_LABELS[c],
            }),
          )}
        />
      </Form.Item>
      <Form.Item
        name="email"
        label="البريد الإلكتروني (اختياري)"
        rules={[{ type: 'email', message: 'بريد إلكتروني غير صحيح' }]}
      >
        <Input inputMode="email" className="ltr-input" autoComplete="off" />
      </Form.Item>
      <Form.Item
        name="committeeRole"
        label="العضوية في اللجنة"
        extra="أعضاء اللجنة الذين لهم بريد إلكتروني يتوصلون بالتقرير الأسبوعي كل يوم اثنين."
      >
        <Select
          allowClear
          placeholder="ليس عضواً في اللجنة"
          options={COMMITTEE_ROLES.map((role) => ({
            value: role,
            label: COMMITTEE_ROLE_LABELS[role],
          }))}
        />
      </Form.Item>

      <Button
        type="primary"
        htmlType="submit"
        block
        className="big-button"
        loading={saving}
      >
        {submitLabel}
      </Button>
      {onCancel && (
        <Button block size="large" onClick={onCancel} className="cancel-button">
          إلغاء
        </Button>
      )}
    </Form>
  );
}
